import type { FC, ReactNode } from 'react'
import type { ServiceSection } from '@/types'

import { useRef } from 'react'
import { Check, Info, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { gsap, useGSAP, MOTION_QUERY } from '@/lib/gsap'
import { Reveal } from '@/components/motion/reveal'
import { AnimatedHeading } from '@/components/motion/animated-heading'

const StepsTimeline: FC<{ items: string[] }> = ({ items }): ReactNode => {
    const ref = useRef<HTMLOListElement>(null)

    useGSAP(() => {
        const list = ref.current
        const line = list?.querySelector('[data-line]')

        if (!list || !line) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            gsap.fromTo(
                line,
                { scaleY: 0 },
                {
                    scaleY: 1,
                    ease: 'none',
                    transformOrigin: 'top center',
                    scrollTrigger: { trigger: list, start: 'top 70%', end: 'bottom 60%', scrub: 0.6 }
                }
            )

            gsap.utils.toArray<HTMLElement>('[data-step]', list).forEach(step => {
                gsap.from(step, {
                    autoAlpha: 0,
                    x: 24,
                    duration: 0.9,
                    ease: 'expo.out',
                    scrollTrigger: { trigger: step, start: 'top 85%', once: true }
                })
            })
        })

        return () => media.revert()
    }, { scope: ref })

    return (
        <ol ref={ref} className='relative'>
            <span aria-hidden='true' className='absolute bottom-6 left-[19px] top-6 w-px bg-ink/10' />
            <span data-line aria-hidden='true' className='absolute bottom-6 left-[19px] top-6 w-px origin-top bg-ember' />

            {items.map((item, index) => (
                <li key={item} data-step className='relative flex gap-6 py-4'>
                    <span className='relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full border border-ink/15 bg-cream font-mono text-[12px] text-ink'>
                        {pad(index + 1)}
                    </span>

                    <span className='pt-2 text-[17px] leading-relaxed text-ink md:text-[18px]'>{item}</span>
                </li>
            ))}
        </ol>
    )
}

interface ServiceBlockProps {
    section: ServiceSection
    index: number
}

export const ServiceBlock: FC<ServiceBlockProps> = ({ section, index }): ReactNode => {
    const content = (() => {
        if (section.type === 'list') {
            return (
                <Reveal as='ul' selector=':scope > li' className='grid gap-3 sm:grid-cols-2'>
                    {section.items.map(item => (
                        <li key={item} className='flex items-start gap-3 rounded-[20px] border border-ink/[0.08] bg-card p-5'>
                            <span className='mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-ember text-white'>
                                <Check className='size-3.5' aria-hidden='true' />
                            </span>

                            <span className='text-[16px] leading-snug text-ink'>{item}</span>
                        </li>
                    ))}
                </Reveal>
            )
        }

        if (section.type === 'steps') return <StepsTimeline items={section.items} />

        if (section.type === 'options') {
            return (
                <Reveal selector=':scope > *' className='grid gap-4 md:grid-cols-3'>
                    {section.items.map((option, optionIndex) => (
                        <div
                            key={option.name}
                            className={cn(
                                'flex flex-col justify-between gap-10 rounded-[26px] p-7',
                                optionIndex === 1 ? 'bg-ink text-cream' : 'border border-ink/[0.08] bg-card text-ink'
                            )}
                        >
                            <div>
                                <span className={cn('font-mono text-[11px] uppercase tracking-[0.16em]', optionIndex === 1 ? 'text-ember-soft' : 'text-slate')}>
                                    Option {pad(optionIndex + 1)}
                                </span>

                                <h3 className='mt-4 font-heading text-[1.6rem] font-semibold leading-tight tracking-[-0.03em]'>
                                    {option.name}
                                </h3>

                                <p className={cn('mt-2 text-[15px] leading-relaxed', optionIndex === 1 ? 'text-cream/65' : 'text-slate')}>
                                    {option.description}
                                </p>
                            </div>

                            <p className={cn('border-t pt-4 font-mono text-[13px]', optionIndex === 1 ? 'border-cream/15' : 'border-ink/10')}>
                                {option.price}
                            </p>
                        </div>
                    ))}
                </Reveal>
            )
        }

        const warning = section.tone === 'warning'
        const Icon = warning ? TriangleAlert : Info

        return (
            <Reveal>
                <div className={cn('flex gap-5 rounded-[26px] p-7 md:p-9', warning ? 'bg-ember-tint' : 'bg-sand/70')}>
                    <span className={cn('flex size-11 shrink-0 items-center justify-center rounded-full', warning ? 'bg-ember text-white' : 'bg-ink text-cream')}>
                        <Icon className='size-5' aria-hidden='true' />
                    </span>

                    <p className='font-heading text-[1.3rem] font-medium leading-[1.4] tracking-[-0.015em] text-ink md:text-[1.5rem]'>
                        {section.body}
                    </p>
                </div>
            </Reveal>
        )
    })()

    return (
        <div className='grid gap-8 border-t border-ink/10 py-14 md:py-20 lg:grid-cols-12 lg:gap-10'>
            <div className='lg:col-span-4'>
                <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-slate'>{pad(index + 1)}</p>

                <AnimatedHeading as='h2' className='mt-3 max-w-[14ch] font-heading text-[2rem] font-semibold leading-[1.05] tracking-[-0.035em] text-ink md:text-[2.5rem]'>
                    {section.title}
                </AnimatedHeading>
            </div>

            <div className='lg:col-span-8'>{content}</div>
        </div>
    )
}
